import { Params } from '@angular/router';
import { AccountPledgeFilters, AccountPledgeQueryParams } from './account-pledge.model';
import {
  FilterParameter,
  EnumHelper,
  MatTableHelper,
  SimpleDialogData,
  ButtonColor,
  CommonActionKey,
  HorizontalListData,
  HorizontalListFactory,
  SimpleHorizontalListWidgetConfigData,
  SimpleHorizontalListBgColorType,
  SimpleHorizontalListAppearanceType,
} from 'common';
import { ProductName } from '../account.model';
import { CurrencyPipe, formatCurrency } from '@angular/common';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Injectable } from '@angular/core';

export const accountPledgeDisplayedColumns: string[] = [
  'selected',
  'alligatorDataIcon',
  'loanNumber',
  'entityName',
  'status',
  'productCode',
  'facilityName',
  'caseName',
  'offerAmount',
  'availableFunds',
];

export const adaptAccountPledgeParams = (data: Params): AccountPledgeQueryParams => {
  return {
    skip: data?.skip || 0,
    limit: data?.limit || 20,
    sort: data?.sort || undefined,
    loanNumber: data?.loanNumber || undefined,
    companyName: data?.companyName || undefined,
    productCode: data?.productCode || undefined,
    offerAmountFrom: data?.offerAmountFrom || undefined,
    offerAmountTo: data?.offerAmountTo || undefined,
  };
};

export const adaptFilters = (query: AccountPledgeQueryParams): FilterParameter[] => {
  if (!query) {
    return [];
  }

  const filters: FilterParameter[] = [];

  if (query.companyName) {
    filters.push({
      id: 'companyName',
      name: 'Company name',
      value: query.companyName.trim(),
    });
  }

  if (query.productCode) {
    filters.push({
      id: 'productCode',
      name: 'Product type',
      value: EnumHelper.getNameFromValue(ProductName, query.productCode),
    });
  }

  if (query.offerAmountFrom) {
    filters.push({
      id: 'offerAmountFrom',
      name: 'Offer amount from',
      value: formatCurrency(query.offerAmountFrom, 'en_US', '$'),
    });
  }

  if (query.offerAmountTo) {
    filters.push({
      id: 'offerAmountTo',
      name: 'Offer amount to',
      value: formatCurrency(query.offerAmountTo, 'en_US', '$'),
    });
  }

  if (query.loanNumber) {
    filters.push({
      id: 'loanNumber',
      name: 'Loan number',
      value: query.loanNumber.trim(),
    });
  }

  return filters;
};

export const adaptQueryParams = (
  filters: AccountPledgeFilters,
  matPaginator: MatPaginator,
  matSort: MatSort,
  selectedFacilityId: number | undefined,
  queryParams: AccountPledgeQueryParams,
): AccountPledgeQueryParams => {
  return {
    limit: matPaginator.pageSize,
    skip: matPaginator.pageIndex * matPaginator.pageSize,
    productCode: filters?.productCode || undefined,
    companyName: filters?.companyName || undefined,
    offerAmountTo: filters?.offerAmountTo || undefined,
    offerAmountFrom: filters?.offerAmountFrom || undefined,
    loanNumber: filters?.loanNumber || undefined,
    selectedFacilityId: selectedFacilityId || undefined,
    sort: matSort && matSort.active ? MatTableHelper.getSort(queryParams.sort, matSort) : undefined,
  };
};

export const confirmationDialogData: SimpleDialogData = {
  title: 'Confirm pledge?',
  body: 'You are pledging the selected loans. Confirm below to move forward with this action.',
  actions: [
    { label: 'CANCEL', buttonColor: ButtonColor.Default, actionKey: CommonActionKey.Cancel },
    { label: 'CONFIRM', buttonColor: ButtonColor.Primary, actionKey: CommonActionKey.Confirm },
  ],
};

@Injectable()
export class AccountPledgeAdapter {
  constructor(private readonly currencyPipe: CurrencyPipe) {}

  adaptListData = (totalCreditLimit: number, totalOutstandingBalance: number): HorizontalListData[] => {
    return [
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Total credit limit',
        displayedValue: this.formatCurrency(totalCreditLimit),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Total outstanding balance',
        displayedValue: this.formatCurrency(totalOutstandingBalance),
      }),
    ];
  };

  private formatCurrency(value: number): string {
    return this.currencyPipe.transform(value);
  }

  adaptListWidgetConfigData = (): SimpleHorizontalListWidgetConfigData => {
    return {
      backgroundColorType: SimpleHorizontalListBgColorType.Grey,
      cardAppearanceType: SimpleHorizontalListAppearanceType.Outlined,
    };
  };
}
